/**
 * calc3LHS — 3-Lite Horizontal Slider cut list calculations
 *
 * Layout (default QHQ): [Lock/Outer | Keeper/Center | Lock/Outer]
 *   Each outer (lock) lite = overallW/4
 *   Center (keeper) lite   = overallW/2
 *
 * Layout (equal thirds): [Equal | Equal | Equal]
 *   All 3 lites = overallW/3
 *   Glass W  = (((lockGlassW × 2) + keepGlassW) / 3) - 2
 *   Glazbead = (((glazLW × 2) + glazKW) / 3)
 *   Sash W   = (((lockSashWCut × 2) + keepSashWCut) / 3) + 0.25
 *
 * Controlled by win.threeHSLayout: 'qhq' (default) | 'thirds'
 */
export function calc3LHS(win) {
  const {
    width: overallW,
    height,
    color           = 'White',
    finType         = 'Without Fin',
    qty             = 1,
    label           = '',
    threeHSLayout   = 'qhq',
    screenType      = 'None',
    screenSize      = 'Full Scrn',
    meshType        = 'STD Mesh',
    gasFill         = 'Argon',
    KeeperGlassPkg  = 'DLA',
    KeeperGlassOpts = 'None',
    KeeperGridType  = 'None',
    KeeperGridPat   = 'None',
    KeeperGridV     = '0',
    KeeperGridH     = '0',
    LockGlassPkg    = 'DLA',
    LockGlassOpts   = 'None',
    LockGridType    = 'None',
    LockGridPat     = 'None',
    LockGridV       = '0',
    LockGridH       = '0',
  } = win;

  const isThirds = threeHSLayout === 'thirds';
  const totalQty = qty;
  const withFin  = finType === 'With Fin' || finType === 'With Fin & J';

  const colorLower    = (color || '').toLowerCase();
  const isDark        = colorLower.includes('woodgrain');
  const glazbeadColor = colorLower.includes('white') ? 'White'
                      : colorLower.includes('clay')  ? 'Clay'
                      : color;

  // ── Grid color helper — matches INSIDE-visible frame color ──────────────
  // Frame naming convention: "Inside/Outside" (e.g. "Dk Woodgrain/White")
  //   - Dk Woodgrain/White → inside is Dk Woodgrain, outside is White
  //   - Woodgrain          → all Woodgrain (fully wrapped)
  //   - White              → all White
  // Grid outside face is ALWAYS White (matches exterior). Grid inside face
  // matches the inside frame color. We report the INSIDE color to Tracy Corners
  // so they know how to build the grid.
  const insideGridColor = color.includes('/')
    ? color.split('/')[0].trim()   // e.g. 'Dk Woodgrain/White' → 'Dk Woodgrain'
    : color;


  // ── Lite widths (QHQ base — used for both layouts) ────────────────────────
  const lockLiteW = overallW / 4;
  const keepLiteW = overallW / 2;

  // ── Sash reference sizes ──────────────────────────────────────────────────
  const sashH_ref     = height    - 4.75;
  const lockSashW_ref = lockLiteW - 0.375;
  const keepSashW_ref = keepLiteW - 1.375;

  // ── QHQ glass sizes ───────────────────────────────────────────────────────
  const keepGlassW_qhq = keepSashW_ref - 2.0625;
  const lockGlassW_qhq = lockSashW_ref - 2.0625;
  const glassH         = sashH_ref - 2;

  // ── QHQ sash cuts ─────────────────────────────────────────────────────────
  const lockSashWCut_qhq = lockSashW_ref + 0.25;
  const keepSashWCut_qhq = keepSashW_ref + 0.25;

  // ── QHQ glazbead widths ───────────────────────────────────────────────────
  const glazKW_qhq = keepSashW_ref - 1.75;
  const glazLW_qhq = lockSashW_ref - 1.75;

  // ── Equal thirds calculated values ────────────────────────────────────────
  // Glass + sash use sash REFERENCE widths as input (not glass widths).
  // Glazbead uses QHQ glazbead widths as input.
  // Verified with 61.75 × 45.5: equalGlassW = 17.875 = 17 7/8 ✓
  const equalGlassW   = ((lockSashW_ref * 2) + keepSashW_ref) / 3 - 2;
  const equalGlazW    = ((glazLW_qhq    * 2) + glazKW_qhq)    / 3;
  const equalSashWCut  = ((lockSashW_ref * 2) + keepSashW_ref) / 3 + 0.25;  // sash cut width for table: 20.125
  const equalSashWeld  = ((lockSashW_ref * 2) + keepSashW_ref) / 3;          // weld size for summary bar: 19.875

  // ── Final values (layout-dependent) ──────────────────────────────────────
  const keepGlassW   = isThirds ? equalGlassW   : keepGlassW_qhq;
  const lockGlassW   = isThirds ? equalGlassW   : lockGlassW_qhq;
  const glazKW       = isThirds ? equalGlazW    : glazKW_qhq;
  const glazLW       = isThirds ? equalGlazW    : glazLW_qhq;
  const lockSashWCut = isThirds ? equalSashWCut : lockSashWCut_qhq;
  const keepSashWCut = isThirds ? equalSashWCut : keepSashWCut_qhq;

  // ── Grid helpers — Keeper ─────────────────────────────────────────────────
  const kGridV    = parseInt(KeeperGridV) || 0;
  const kGridH    = parseInt(KeeperGridH) || 0;
  const kHasGrids = !!KeeperGridType && KeeperGridType !== 'None' && KeeperGridType !== '' && (kGridV > 0 || kGridH > 0);
  const kGridLenH  = kHasGrids ? keepGlassW - 1 : null;
  const kGridLenV  = kHasGrids ? glassH - 1     : null;
  const kHorizQty  = kHasGrids ? kGridH * totalQty : 0;
  const kVertQty   = kHasGrids ? kGridV * totalQty : 0;
  const kGridColor = kHasGrids ? insideGridColor : '';

  // ── Grid helpers — Lock ───────────────────────────────────────────────────
  const lGridV    = parseInt(LockGridV) || 0;
  const lGridH    = parseInt(LockGridH) || 0;
  const lHasGrids = !!LockGridType && LockGridType !== 'None' && LockGridType !== '' && (lGridV > 0 || lGridH > 0);
  const lGridLenH  = lHasGrids ? lockGlassW - 1 : null;
  const lGridLenV  = lHasGrids ? glassH - 1     : null;
  const lHorizQty  = lHasGrids ? lGridH * totalQty : 0;
  const lVertQty   = lHasGrids ? lGridV * totalQty : 0;
  const lGridColor = lHasGrids ? insideGridColor : '';

  // ── Notch ─────────────────────────────────────────────────────────────────
  const notch = height <= 31.9375 ? "4'' Notch" : "7'' Notch";

  // ── Frame (same for both layouts) ─────────────────────────────────────────
  const frameW = overallW + 0.25;
  const frameH = height   + 0.25;

  // ── Rail / reinf / track / starter (same for both layouts) ───────────────
  const railLen        = sashH_ref + 0.3125;
  const reinfLen       = height - 6.6875;
  const trackFillerLen = overallW - 3.5;
  const starterQty     = withFin ? 0 : totalQty;
  const starterPart    = `8351-${glazbeadColor}`;

  const snapMullQtyMap = { Single:0,'2-Lite':1,'3-Lite':2,'4-Lite':3,'5-Lite':4,'Q/H/Q':2,Twin:1,Triple:2,Quad:3,Quin:4 };
  const snapMullQty = (snapMullQtyMap[win.config] || 0) * qty;
  const snapMullLen = height - 0.125;

  // ── Glazbead height (same for both layouts) ───────────────────────────────
  const glazH        = sashH_ref - 2.9375;
  const glazbeadPart = `5457-${glazbeadColor}`;

  // ── Screens (same for both layouts) ──────────────────────────────────────
  const isFlexScrn  = screenType === 'Flex Scrn';
  const isStdScrn   = screenType === 'Std Scrn';
  const flexScreenW = overallW / 4 - 1.25;
  const flexScreenH = height - 4.375;
  const flexScreen  = isFlexScrn ? { qty: totalQty * 2, width: flexScreenW, height: flexScreenH, meshType: meshType || 'STD Mesh', screenType, label } : null;
  const stdScreenH     = height    - 6.375;
  const stdScreenW     = lockLiteW - 2.625;
  const screenPart     = isDark ? `SCRNPLN-${glazbeadColor}`  : `SCRNPLN-${color}`;
  const screenPullPart = isDark ? `SCRNPULL-${glazbeadColor}` : `SCRNPULL-${color}`;

  // ── Glass panes ───────────────────────────────────────────────────────────
  const glassPanes = isThirds
    ? [{
        label:       'Equal Glass',
        qty:         totalQty * 3,
        width:       equalGlassW,
        height:      glassH,
        glassType:   KeeperGlassPkg,
        glassConfig: KeeperGlassOpts !== 'None' ? KeeperGlassOpts : '',
        gasFill,
        grids:       kHasGrids,
        gridType:    kHasGrids ? KeeperGridType : '',
        gridPattern: kHasGrids ? (KeeperGridPat === 'None' ? '' : KeeperGridPat) : '',
        gridQtyV:    kHasGrids ? kGridV : 0,
        gridQtyH:    kHasGrids ? kGridH : 0,
        gridColor:   kHasGrids ? kGridColor : '',
        gridLenH:    kGridLenH,
        gridLenV:    kGridLenV,
        horizQty:    kHorizQty,
        vertQty:     kVertQty,
      }]
    : [
        {
          label:       'Keeper Glass',
          qty:         totalQty,
          width:       keepGlassW,
          height:      glassH,
          glassType:   KeeperGlassPkg,
          glassConfig: KeeperGlassOpts !== 'None' ? KeeperGlassOpts : '',
          gasFill,
          grids:       kHasGrids,
          gridType:    kHasGrids ? KeeperGridType : '',
          gridPattern: kHasGrids ? (KeeperGridPat === 'None' ? '' : KeeperGridPat) : '',
          gridQtyV:    kHasGrids ? kGridV : 0,
          gridQtyH:    kHasGrids ? kGridH : 0,
          gridColor:   kHasGrids ? kGridColor : '',
          gridLenH:    kGridLenH,
          gridLenV:    kGridLenV,
          horizQty:    kHorizQty,
          vertQty:     kVertQty,
        },
        {
          label:       'Lock Glass',
          qty:         totalQty * 2,
          width:       lockGlassW,
          height:      glassH,
          glassType:   LockGlassPkg,
          glassConfig: LockGlassOpts !== 'None' ? LockGlassOpts : '',
          gasFill,
          grids:       lHasGrids,
          gridType:    lHasGrids ? LockGridType : '',
          gridPattern: lHasGrids ? (LockGridPat === 'None' ? '' : LockGridPat) : '',
          gridQtyV:    lHasGrids ? lGridV : 0,
          gridQtyH:    lHasGrids ? lGridH : 0,
          gridColor:   lHasGrids ? lGridColor : '',
          gridLenH:    lGridLenH,
          gridLenV:    lGridLenV,
          horizQty:    lHorizQty,
          vertQty:     lVertQty,
        },
      ];

  // ── Sash rows ─────────────────────────────────────────────────────────────
  const sashRows = isThirds
    ? [
        { label: 'Sash Widths (Equal)', qty: totalQty * 6, length: equalSashWCut, part: `5540-${color}` },
        { label: 'Keeper Rail',         qty: totalQty * 2, length: railLen,       part: `5543-${color}` },
        { label: 'Lock Lift Rail',      qty: totalQty * 2, length: railLen,       part: `5541-${color}` },
        { label: 'Lock Rail',           qty: totalQty * 2, length: railLen,       part: `5542-${color}` },
      ]
    : [
        { label: 'Sash Widths Lock', qty: totalQty * 4, length: lockSashWCut, part: `5540-${color}` },
        { label: 'Sash Widths Kpr',  qty: totalQty * 2, length: keepSashWCut, part: `5540-${color}` },
        { label: 'Keeper Rail',      qty: totalQty * 2, length: railLen,      part: `5543-${color}` },
        { label: 'Lock Lift Rail',   qty: totalQty * 2, length: railLen,      part: `5541-${color}` },
        { label: 'Lock Rail',        qty: totalQty * 2, length: railLen,      part: `5542-${color}` },
      ];

  // ── Glazbead rows ─────────────────────────────────────────────────────────
  const glazbeadRows = isThirds
    ? [
        { label: '(x2) Glazbead Widths (Equal)', qty: totalQty * 3, length: equalGlazW, part: glazbeadPart },
        { label: '(x2) Glazbead Heights',        qty: totalQty * 3, length: glazH,      part: glazbeadPart },
      ]
    : [
        { label: '(x2) Glazbead K Width',  qty: totalQty,     length: glazKW, part: glazbeadPart },
        { label: '(x2) Glazbead L Widths', qty: totalQty * 2, length: glazLW, part: glazbeadPart },
        { label: '(x2) Glazbead Heights',  qty: totalQty * 3, length: glazH,  part: glazbeadPart },
      ];

  return {
    label,
    type:         '3LHS',
    color,
    overallW,
    width:        overallW,
    height,
    config:       'Single',
    threeHSLayout,
    qty,
    totalQty,
    finType,
    screenType,
    screenSize,
    glass:         glassPanes,
    frame: [
      { label: '3-Lite Frame Widths',  qty: totalQty * 2, length: frameW, part: `5153-${color}` },
      { label: '3-Lite Frame Heights', qty: totalQty * 2, length: frameH, part: `5153-${color}` },
    ],
    sash:          sashRows,
    // Lock + Keeper reinf are the identical cut (2 of each on a 3-lite) — one
    // "(x4)" line instead of two "(x2)" rows.
    reinforcement: [
      { label: '(x4) 665 Lock/Keeper Reinf', qty: totalQty * 4, length: reinfLen, part: '665' },
    ],
    glazbead:      glazbeadRows,
    misc: [
      { label: '(x2) Slider Track Filler', qty: totalQty * 2, length: trackFillerLen, part: `5660-${color}` },
      ...(starterQty > 0 ? [{ label: 'Starter', qty: starterQty, length: overallW, part: starterPart }] : []),
      ...(snapMullQty > 0 ? [{ label: '(x2) Snap Mull', qty: snapMullQty, length: snapMullLen, part: `8309-${color}` }] : []),
    ],
    balance: { notch, qty: totalQty * 4 },
    flexScreen,
    stdScreen: isStdScrn ? {
      height: stdScreenH, width: stdScreenW,
      heightQty: totalQty * 4, withPullQty: totalQty * 2, noPullQty: totalQty * 2,
      screenPart, screenPullPart, label,
    } : null,
    summary: {
      lockSashW: lockSashW_ref, keepSashW: keepSashW_ref,
      sashHeight: sashH_ref, sashWidth: lockSashW_ref,
      lockGlassW, keepGlassW, glassH,
      ...(isThirds ? { equalGlassW, equalGlazW, equalSashWCut, equalSashWeld } : {}),
    },
  };
}